import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HStack } from "@design-system/HStack/HStack";
import { isAxiosError } from "axios";
import { useAccountMergingMutation } from "@mutations/financialAccount";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Entity } from "@common/types/entity";
import { StandardModal } from "@design-system/Modal/Modal";
import { FinancialAccount } from "@common/types/financialAccount";
import {
    ConflictWhenMerging,
    MergeFinancialAccountsResponse,
} from "@common/dto/financialAccount/mergeFinancialAccounts/merge-financial-accounts-response.dto";
import { getAccountNameWithLast4 } from "@common/helpers/financialAccount";
import { pluralize } from "@common/helpers/string";
import { Transaction } from "@common/types/transaction";
import { BothModifiedConflict } from "@common/dto/financialAccount/mergeFinancialAccounts/both-modified-conflict.dto";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { MultipleMatchingConflicts } from "./MultipleMatchingConflicts";
import { BothModifiedConflicts } from "./BothModifiedConflicts";
import { DeduplicatedTransactionsPreview } from "./DeduplicatedTransactionsPreview";
import { MergeBlockingConflicts } from "./MergeBlockingConflicts";
import { ProtectedPeriodsWarningModal } from "./ProtectedPeriodsWarningModal";
import {
    AdditionalMergeConflictModal,
    AdditionalMergeConflictModalResult,
} from "./AdditionalMergeConflictModal";
import {
    AutomaticRulesPreview,
    MergedAccountsToast,
} from "./AutomaticRulesPreview";

const RETRY_GUARD_DURATION_MS = 10 * 60 * 1000;

interface ExecuteMergeOptions {
    bothModifiedConflictsResolutions: number[];
    multipleMatchingConflictsResolutions: Array<{
        fromDestination: number;
        toKeep: number;
    }>;
}

interface Props {
    destinationAccount: FinancialAccount;
    accountToMerge: FinancialAccount;
    entity: Entity;
    mergePreview: MergeFinancialAccountsResponse;
    dateMatchWindowDays?: number;
    onReset: () => void;
    onFinish: () => void;
}

export const MergePreview: React.FC<Props> = ({
    destinationAccount,
    accountToMerge,
    entity,
    mergePreview,
    dateMatchWindowDays,
    onReset,
    onFinish,
}) => {
    const mergeMutation = useAccountMergingMutation();
    const { toast } = useToaster();
    const destinationAccountName = getAccountNameWithLast4(
        destinationAccount,
        2,
    );
    const mergedAccountName = getAccountNameWithLast4(accountToMerge, 2);
    const [merging, setMerging] = useState(false);
    const [
        bothModifiedConflictsResolutions,
        setBothModifiedConflictsResolutions,
    ] = useState<number[]>([]);

    const [
        multipleMatchingConflictsResolutions,
        setMultipleMatchingConflictsResolutions,
    ] = useState<
        Array<{
            fromDestination: number;
            toKeep: number;
        }>
    >([]);

    const [additionalMergeConflict, setAdditionalMergeConflict] = useState<
        ConflictWhenMerging | undefined
    >();

    const [showProtectedWarning, setShowProtectedWarning] = useState(false);

    const isMergeBlocked = mergePreview.dualReconciliationConflicts.length > 0;
    const hasProtectedPeriodsWarning =
        mergePreview.protectedPeriodsWarning.hasProtectedData;
    const hasLockedNonDuplicates =
        mergePreview.lockedNonDuplicateTransactionsNumber > 0;

    const [retryGuardUntil, setRetryGuardUntil] = useState<number | null>(null);
    const [now, setNow] = useState<number>(() => Date.now());

    useEffect(() => {
        if (retryGuardUntil === null) {
            return;
        }
        const interval = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(interval);
    }, [retryGuardUntil]);

    const isRetryGuardActive =
        retryGuardUntil !== null && retryGuardUntil > now;
    const retryGuardMinutesLeft = isRetryGuardActive
        ? Math.max(1, Math.ceil((retryGuardUntil - now) / 60_000))
        : 0;

    const executeMerge = useCallback(
        async (options: ExecuteMergeOptions) => {
            if (!destinationAccount) {
                return;
            }

            try {
                const response = await mergeMutation.mutateAsync({
                    accountToMergeId: accountToMerge.id,
                    destinationAccountId: destinationAccount.id,
                    entityId: entity.id,
                    dryRun: false,
                    dateMatchWindowDays,
                    unlockPeriod: hasLockedNonDuplicates,
                    ...options,
                });

                if (response.conflictWhenMerging) {
                    setAdditionalMergeConflict(response.conflictWhenMerging);
                } else {
                    toast(
                        <MergedAccountsToast
                            automaticRules={response.automaticRules}
                        />,
                    );
                    onFinish();
                }
            } catch (error) {
                if (isAxiosError(error) && error.response?.status === 409) {
                    setRetryGuardUntil(Date.now() + RETRY_GUARD_DURATION_MS);
                    setNow(Date.now());
                    return;
                }
                throw error;
            }
        },
        [
            destinationAccount,
            mergeMutation,
            accountToMerge.id,
            entity.id,
            dateMatchWindowDays,
            hasLockedNonDuplicates,
            onFinish,
            toast,
        ],
    );

    const bothModifiedConflictsResolved = useMemo(
        () =>
            mergePreview.bothModifiedConflicts.every(
                (conflict) =>
                    bothModifiedConflictsResolutions.includes(
                        conflict.transactionFromDestination.id,
                    ) ||
                    bothModifiedConflictsResolutions.includes(
                        conflict.transactionFromMerged.id,
                    ),
            ),
        [mergePreview.bothModifiedConflicts, bothModifiedConflictsResolutions],
    );

    const mergeDisabled =
        isMergeBlocked ||
        mergePreview.multipleMatchingConflicts.length >
            multipleMatchingConflictsResolutions.length ||
        (mergePreview.bothModifiedConflicts.length > 0 &&
            !bothModifiedConflictsResolved) ||
        isRetryGuardActive;
    let mergeMessage;

    if (isRetryGuardActive) {
        mergeMessage = `Account merge is still processing. Please wait ~${retryGuardMinutesLeft} ${pluralize(
            "minute",
            retryGuardMinutesLeft,
        )} before retrying.`;
    } else if (isMergeBlocked) {
        mergeMessage = "Resolve the blocking issues above before merging.";
    } else if (mergeDisabled) {
        mergeMessage = "Please resolve conflicts before merging.";
    }

    const handleMergeClick = useCallback(() => {
        if (hasProtectedPeriodsWarning) {
            setShowProtectedWarning(true);
            return;
        }
        return executeMerge({
            bothModifiedConflictsResolutions,
            multipleMatchingConflictsResolutions,
        });
    }, [
        hasProtectedPeriodsWarning,
        executeMerge,
        bothModifiedConflictsResolutions,
        multipleMatchingConflictsResolutions,
    ]);

    const handleConfirmProtectedMerge = useCallback(async () => {
        await executeMerge({
            bothModifiedConflictsResolutions,
            multipleMatchingConflictsResolutions,
        }).finally(() => {
            setShowProtectedWarning(false);
        });
    }, [
        executeMerge,
        bothModifiedConflictsResolutions,
        multipleMatchingConflictsResolutions,
    ]);

    const resolveBothModifiedConflict = useCallback(
        (conflict: BothModifiedConflict, resolution: Transaction) => {
            setBothModifiedConflictsResolutions((prev) => {
                const newResolutions = [...prev].filter(
                    (id) =>
                        id !== conflict.transactionFromDestination.id &&
                        id !== conflict.transactionFromMerged.id,
                );
                newResolutions.push(resolution.id);

                return newResolutions;
            });
        },
        [],
    );

    const resolveAdditionalConflict = useCallback(
        (result?: AdditionalMergeConflictModalResult) => {
            if (result) {
                const {
                    bothModifiedConflictResolution,
                    multipleMatchingConflictResolution,
                } = result;
                const newBothModifiedConflictsResolutions = [
                    ...bothModifiedConflictsResolutions,
                ];
                const newMultipleMatchingConflictsResolutions = [
                    ...multipleMatchingConflictsResolutions,
                ];

                if (bothModifiedConflictResolution) {
                    newBothModifiedConflictsResolutions.push(
                        bothModifiedConflictResolution,
                    );
                    setBothModifiedConflictsResolutions(
                        newBothModifiedConflictsResolutions,
                    );
                }

                if (multipleMatchingConflictResolution) {
                    newMultipleMatchingConflictsResolutions.push(
                        multipleMatchingConflictResolution,
                    );
                    setMultipleMatchingConflictsResolutions(
                        newMultipleMatchingConflictsResolutions,
                    );
                }

                executeMerge({
                    bothModifiedConflictsResolutions:
                        newBothModifiedConflictsResolutions,
                    multipleMatchingConflictsResolutions:
                        newMultipleMatchingConflictsResolutions,
                });
                setAdditionalMergeConflict(undefined);
            } else {
                onFinish();
            }
        },
        [
            onFinish,
            bothModifiedConflictsResolutions,
            multipleMatchingConflictsResolutions,
            executeMerge,
        ],
    );

    return (
        <>
            {mergePreview.movedTransactionsNumber > 0 && (
                <p>
                    {mergePreview.movedTransactionsNumber}{" "}
                    {pluralize(
                        "transaction",
                        mergePreview.movedTransactionsNumber,
                    )}{" "}
                    will be moved to <strong>{destinationAccountName}</strong>
                </p>
            )}

            {mergePreview.outOfWindowMovedTransactionsNumber > 0 && (
                <p className="small">
                    {mergePreview.outOfWindowMovedTransactionsNumber} additional{" "}
                    {pluralize(
                        "transaction",
                        mergePreview.outOfWindowMovedTransactionsNumber,
                    )}{" "}
                    outside the bookkeeping window will also be moved to{" "}
                    <strong>{destinationAccountName}</strong> and won&apos;t
                    affect reports.
                </p>
            )}

            {isMergeBlocked && (
                <MergeBlockingConflicts
                    dualReconciliationConflicts={
                        mergePreview.dualReconciliationConflicts
                    }
                />
            )}

            {mergePreview.deduplicatedTransactions.length > 0 && (
                <div className="mb-4">
                    <DeduplicatedTransactionsPreview
                        duplicates={mergePreview.deduplicatedTransactions}
                    />
                </div>
            )}

            {mergePreview.multipleMatchingConflicts.length > 0 && (
                <div className="mb-4">
                    <MultipleMatchingConflicts
                        conflicts={mergePreview.multipleMatchingConflicts}
                        multipleMatchingConflictsResolutions={
                            multipleMatchingConflictsResolutions
                        }
                        onResolve={setMultipleMatchingConflictsResolutions}
                    />
                </div>
            )}

            {mergePreview.bothModifiedConflicts.length > 0 && (
                <div className="mb-4">
                    <BothModifiedConflicts
                        conflicts={mergePreview.bothModifiedConflicts}
                        resolutions={bothModifiedConflictsResolutions}
                        onResolve={resolveBothModifiedConflict}
                    />
                </div>
            )}

            <AutomaticRulesPreview
                automaticRules={mergePreview.automaticRules}
                destinationAccountName={destinationAccountName}
            />

            <p>
                <strong>{mergedAccountName}</strong> account will be removed.
            </p>

            <HStack
                as="footer"
                justify="between"
                align="start"
                className="mt-4"
            >
                <Button variant="secondary" size="md" onClick={onReset}>
                    Cancel
                </Button>
                <div className="text-right">
                    <ButtonWithLoader
                        variant="primary"
                        size="md"
                        onClick={handleMergeClick}
                        loading={merging}
                        setLoading={setMerging}
                        disabled={mergeDisabled}
                    >
                        Merge
                    </ButtonWithLoader>
                    {mergeMessage && (
                        <p className="small mb-0 mt-2">{mergeMessage}</p>
                    )}
                </div>
            </HStack>

            <StandardModal
                show={!!additionalMergeConflict}
                backdrop="static"
                size="xl"
            >
                {additionalMergeConflict && (
                    <AdditionalMergeConflictModal
                        conflict={additionalMergeConflict}
                        close={resolveAdditionalConflict}
                    />
                )}
            </StandardModal>

            <StandardModal show={showProtectedWarning} backdrop="static">
                <ProtectedPeriodsWarningModal
                    warning={mergePreview.protectedPeriodsWarning}
                    lockedTransactions={
                        mergePreview.lockedNonDuplicateTransactions
                    }
                    lockedTransactionsNumber={
                        mergePreview.lockedNonDuplicateTransactionsNumber
                    }
                    destinationAccountName={destinationAccountName}
                    mergedAccountName={mergedAccountName}
                    onCancel={() => setShowProtectedWarning(false)}
                    onContinue={handleConfirmProtectedMerge}
                />
            </StandardModal>
        </>
    );
};
